import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import MainLayout from '../components/MainLayout';
import { Button } from '../components/ui/button';
import {
  BookOpen,
  Calendar,
  CheckCircle2,
  Clock,
  Download,
  Eye,
  Info,
  X,
  Loader2,
  Filter,
  CheckCircle,
  XCircle,
  AlertCircle,
  Search,
  RefreshCw,
  Landmark,
  CreditCard,
  Building2,
  TrendingUp,
  Receipt,
  FileSpreadsheet,
  ArrowRight,
  Wallet,
  MapPin,
  Plane,
  FileText,
  FileCode,
  Paperclip,
  ExternalLink,
  DollarSign
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_BADGES = {
  'Paid': { label: 'Paid', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  'Pay Pending': { label: 'Pay Pending', cls: 'bg-amber-100 text-amber-800 border-amber-300' },
  'Approved': { label: 'Approved', cls: 'bg-blue-100 text-blue-800 border-blue-300' },
  'Pending Review': { label: 'Pending Review', cls: 'bg-slate-100 text-slate-700 border-slate-300' },
  'Pending': { label: 'Pending', cls: 'bg-slate-100 text-slate-700 border-slate-300' },
  'Rejected': { label: 'Rejected', cls: 'bg-rose-100 text-rose-800 border-rose-300' },
};

const CLAIM_TYPE_BADGES = {
  'Local Conveyance': { cls: 'bg-teal-50 text-teal-700 border-teal-200', icon: MapPin },
  'Travel Advance': { cls: 'bg-indigo-50 text-indigo-700 border-indigo-200', icon: Wallet },
  'Self Booking Reimbursement': { cls: 'bg-purple-50 text-purple-700 border-purple-200', icon: Receipt },
  'Company Booked Travel': { cls: 'bg-sky-50 text-sky-700 border-sky-200', icon: Plane },
  'Outstation Conveyance': { cls: 'bg-sky-50 text-sky-700 border-sky-200', icon: Plane },
};

const ESSConveyanceLedger = () => {
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [ledger, setLedger] = useState([]);
  const [summary, setSummary] = useState({
    total_requests: 0,
    total_claimed: 0,
    total_approved: 0,
    total_paid: 0,
    pending_approval_amount: 0,
    pending_reimbursement_amount: 0,
  });

  // Filter States
  const [conveyanceType, setConveyanceType] = useState('all'); // all | local | travel
  const [approvalStatus, setApprovalStatus] = useState('all');
  const [paymentStatus, setPaymentStatus] = useState('all');
  const [travelMode, setTravelMode] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [search, setSearch] = useState('');

  // Selected Detail Modal State
  const [selectedRecord, setSelectedRecord] = useState(null);

  // Fetch Employee Ledger
  const fetchLedger = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (conveyanceType && conveyanceType !== 'all') params.append('conveyance_type', conveyanceType);
      if (approvalStatus && approvalStatus !== 'all') params.append('approval_status', approvalStatus);
      if (paymentStatus && paymentStatus !== 'all') params.append('payment_status', paymentStatus);
      if (travelMode && travelMode !== 'all') params.append('travel_mode', travelMode);
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);
      if (search) params.append('search', search);

      const res = await axios.get(`${API_URL}/travel-desk/my-conveyance-ledger?${params.toString()}`, {
        withCredentials: true
      });

      if (res.data && res.data.success) {
        setLedger(res.data.ledger || []);
        setSummary(res.data.summary || {});
      }
    } catch (err) {
      console.error('Failed to fetch conveyance ledger:', err);
      toast.error('Failed to load your Conveyance Ledger');
    } finally {
      setLoading(false);
    }
  }, [conveyanceType, approvalStatus, paymentStatus, travelMode, fromDate, toDate, search]);

  useEffect(() => {
    fetchLedger();
  }, [fetchLedger]);

  // Handle Export (Excel, PDF, CSV)
  const handleExport = async (format = 'excel') => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      params.append('export_type', format);
      if (conveyanceType && conveyanceType !== 'all') params.append('conveyance_type', conveyanceType);
      if (approvalStatus && approvalStatus !== 'all') params.append('approval_status', approvalStatus);
      if (paymentStatus && paymentStatus !== 'all') params.append('payment_status', paymentStatus);
      if (travelMode && travelMode !== 'all') params.append('travel_mode', travelMode);
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);
      if (search) params.append('search', search);

      const url = `${API_URL}/travel-desk/my-conveyance-ledger/export?${params.toString()}`;
      window.open(url, '_blank');
      toast.success(`${format.toUpperCase()} export initiated successfully`);
    } catch (err) {
      console.error('Export failed:', err);
      toast.error(`Failed to export ledger to ${format.toUpperCase()}`);
    } finally {
      setExporting(false);
    }
  };

  // Reset Filters
  const handleResetFilters = () => {
    setConveyanceType('all');
    setApprovalStatus('all');
    setPaymentStatus('all');
    setTravelMode('all');
    setFromDate('');
    setToDate('');
    setSearch('');
  };

  const formatCurrency = (val) => {
    const n = Number(val) || 0;
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(n);
  };

  return (
    <MainLayout title="My Conveyance Ledger">
      <div className="p-4 md:p-6 space-y-6 bg-slate-50 min-h-screen">
        
        {/* Top Header Card */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-5 rounded-2xl shadow-sm border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-gradient-to-tr from-emerald-600 to-teal-600 text-white rounded-xl shadow-md">
              <BookOpen className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900 tracking-tight">My Conveyance Ledger</h1>
              <p className="text-xs text-slate-500 mt-0.5">
                Complete personal history of Local Conveyance & Travel/Outstation claims, expense details, approvals & payment receipts.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={fetchLedger}
              disabled={loading}
              className="border-slate-300 text-slate-700 hover:bg-slate-100"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>

            {/* Export Actions */}
            <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-xl border border-slate-200">
              <Button
                size="sm"
                onClick={() => handleExport('excel')}
                disabled={exporting}
                className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8 px-3"
              >
                <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" />
                Excel
              </Button>
              <Button
                size="sm"
                onClick={() => handleExport('pdf')}
                disabled={exporting}
                className="bg-rose-600 hover:bg-rose-700 text-white text-xs h-8 px-3"
              >
                <FileText className="w-3.5 h-3.5 mr-1.5" />
                PDF
              </Button>
              <Button
                size="sm"
                onClick={() => handleExport('csv')}
                disabled={exporting}
                className="bg-slate-700 hover:bg-slate-800 text-white text-xs h-8 px-3"
              >
                <FileCode className="w-3.5 h-3.5 mr-1.5" />
                CSV
              </Button>
            </div>
          </div>
        </div>

        {/* Dynamic Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {/* Total Requests */}
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
            <div className="text-slate-500 text-[11px] font-semibold uppercase tracking-wider">Total Requests</div>
            <div className="text-xl font-bold text-slate-900 mt-1">{summary.total_requests || summary.total_claims || ledger.length}</div>
            <div className="text-[10px] text-slate-400 mt-1">Conveyance Claims</div>
          </div>

          {/* Total Claimed */}
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
            <div className="text-slate-500 text-[11px] font-semibold uppercase tracking-wider">Total Claimed</div>
            <div className="text-lg font-bold text-slate-900 mt-1">{formatCurrency(summary.total_claimed)}</div>
            <div className="text-[10px] text-slate-400 mt-1">Submitted Expenses</div>
          </div>

          {/* Total Approved */}
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
            <div className="text-emerald-600 text-[11px] font-semibold uppercase tracking-wider">Total Approved</div>
            <div className="text-lg font-bold text-emerald-700 mt-1">{formatCurrency(summary.total_approved)}</div>
            <div className="text-[10px] text-slate-400 mt-1">Approved Claims</div>
          </div>

          {/* Total Paid */}
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
            <div className="text-blue-600 text-[11px] font-semibold uppercase tracking-wider">Total Paid</div>
            <div className="text-lg font-bold text-blue-700 mt-1">{formatCurrency(summary.total_paid || summary.total_paid_company)}</div>
            <div className="text-[10px] text-slate-400 mt-1">Reimbursed / Paid</div>
          </div>

          {/* Pending Approval */}
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
            <div className="text-amber-600 text-[11px] font-semibold uppercase tracking-wider">Pending Approval</div>
            <div className="text-lg font-bold text-amber-700 mt-1">{formatCurrency(summary.pending_approval_amount)}</div>
            <div className="text-[10px] text-slate-400 mt-1">Under Review</div>
          </div>

          {/* Pending Reimbursement */}
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
            <div className="text-purple-600 text-[11px] font-semibold uppercase tracking-wider">Pending Payment</div>
            <div className="text-lg font-bold text-purple-700 mt-1">{formatCurrency(summary.pending_reimbursement_amount || summary.total_pending_payment)}</div>
            <div className="text-[10px] text-slate-400 mt-1">Finance Queue</div>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-200 space-y-4">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <div className="flex items-center gap-2 font-semibold text-slate-800 text-sm">
              <Filter className="w-4 h-4 text-emerald-600" />
              <span>Conveyance Filters</span>
            </div>
            <button
              onClick={handleResetFilters}
              className="text-xs text-blue-600 hover:text-blue-800 font-medium hover:underline"
            >
              Clear / Reset Filters
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {/* Search Keyword */}
            <div className="col-span-1 lg:col-span-2 relative">
              <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider block mb-1">Search Keywords</label>
              <div className="relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Request ID / Location / Purpose / UTR..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-9 pr-3 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>

            {/* Conveyance Category */}
            <div>
              <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider block mb-1">Category</label>
              <select
                value={conveyanceType}
                onChange={(e) => setConveyanceType(e.target.value)}
                className="w-full px-2.5 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 font-medium"
              >
                <option value="all">All Conveyance Types</option>
                <option value="local">Local Conveyance</option>
                <option value="travel">Travel / Outstation Conveyance</option>
              </select>
            </div>

            {/* Approval Status */}
            <div>
              <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider block mb-1">Approval Status</label>
              <select
                value={approvalStatus}
                onChange={(e) => setApprovalStatus(e.target.value)}
                className="w-full px-2.5 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="all">All Approval Statuses</option>
                <option value="pending">Pending Review</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            {/* Payment Status */}
            <div>
              <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider block mb-1">Payment Status</label>
              <select
                value={paymentStatus}
                onChange={(e) => setPaymentStatus(e.target.value)}
                className="w-full px-2.5 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="all">All Payment Statuses</option>
                <option value="pending">Pending Payment</option>
                <option value="pay_pending">Pay Pending</option>
                <option value="paid">Paid / Reimbursed</option>
              </select>
            </div>

            {/* Travel Mode */}
            <div>
              <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider block mb-1">Travel Mode</label>
              <select
                value={travelMode}
                onChange={(e) => setTravelMode(e.target.value)}
                className="w-full px-2.5 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="all">All Modes</option>
                <option value="cab">Cab / Taxi</option>
                <option value="auto">Auto / Rickshaw</option>
                <option value="bus">Bus</option>
                <option value="train">Train</option>
                <option value="flight">Flight</option>
                <option value="own">Own Vehicle</option>
              </select>
            </div>

            {/* Travel Date From */}
            <div>
              <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider block mb-1">Date From</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-full px-2.5 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            {/* Travel Date To */}
            <div>
              <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider block mb-1">Date To</label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="w-full px-2.5 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
          </div>
        </div>

        {/* 20-Column Data Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between">
            <div className="font-bold text-slate-900 text-sm flex items-center gap-2">
              <Receipt className="w-4 h-4 text-emerald-600" />
              <span>Conveyance Ledger History ({ledger.length} records)</span>
            </div>
          </div>

          {loading ? (
            <div className="p-12 text-center text-slate-500 flex flex-col items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-600 mb-2" />
              <p className="text-xs font-medium">Loading your Conveyance Ledger records...</p>
            </div>
          ) : ledger.length === 0 ? (
            <div className="p-12 text-center text-slate-500">
              <AlertCircle className="w-10 h-10 text-slate-300 mx-auto mb-2" />
              <p className="text-sm font-semibold text-slate-700">No Conveyance Records Available</p>
              <p className="text-xs text-slate-400 mt-1">No conveyance claims match your active filters or date range.</p>
            </div>
          ) : (
            <div className="overflow-x-auto max-w-full">
              <table className="w-full text-left text-xs border-collapse min-w-[1800px]">
                <thead className="bg-slate-900 text-slate-200 font-semibold uppercase text-[10px] tracking-wider sticky top-0 z-10">
                  <tr>
                    <th className="p-3 border-b border-slate-800 text-center w-12">S.No</th>
                    <th className="p-3 border-b border-slate-800">Request ID</th>
                    <th className="p-3 border-b border-slate-800">Conveyance Type</th>
                    <th className="p-3 border-b border-slate-800">Travel Date</th>
                    <th className="p-3 border-b border-slate-800">From Location</th>
                    <th className="p-3 border-b border-slate-800">To Location</th>
                    <th className="p-3 border-b border-slate-800">Purpose</th>
                    <th className="p-3 border-b border-slate-800">Travel Mode</th>
                    <th className="p-3 border-b border-slate-800 text-center">Distance (KM)</th>
                    <th className="p-3 border-b border-slate-800 text-right">Expense Amount (₹)</th>
                    <th className="p-3 border-b border-slate-800 text-right">Approved Amount (₹)</th>
                    <th className="p-3 border-b border-slate-800 text-right">Advance Amount (₹)</th>
                    <th className="p-3 border-b border-slate-800 text-right">Reimbursement (₹)</th>
                    <th className="p-3 border-b border-slate-800 text-center">Attachments</th>
                    <th className="p-3 border-b border-slate-800">Submitted Date</th>
                    <th className="p-3 border-b border-slate-800 text-center">Approval Status</th>
                    <th className="p-3 border-b border-slate-800 text-center">Payment Status</th>
                    <th className="p-3 border-b border-slate-800">Payment Date</th>
                    <th className="p-3 border-b border-slate-800">Remarks</th>
                    <th className="p-3 border-b border-slate-800 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 font-normal text-slate-700 bg-white">
                  {ledger.map((item, idx) => {
                    const stBadge = STATUS_BADGES[item.payment_status] || STATUS_BADGES[item.approval_status] || STATUS_BADGES['Pending Review'];
                    const ctBadge = CLAIM_TYPE_BADGES[item.claim_type] || CLAIM_TYPE_BADGES['Local Conveyance'];
                    const CtIcon = ctBadge.icon;
                    const attachCount = (item.attachments || []).length;

                    return (
                      <tr key={item.id || idx} className="hover:bg-slate-50/80 transition-colors">
                        <td className="p-2.5 border-b border-slate-100 text-center font-mono text-slate-400">
                          {idx + 1}
                        </td>
                        <td className="p-2.5 border-b border-slate-100 font-mono font-semibold text-blue-700">
                          {item.transaction_id}
                        </td>
                        <td className="p-2.5 border-b border-slate-100">
                          <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${ctBadge.cls}`}>
                            <CtIcon className="w-3 h-3" />
                            {item.claim_type}
                          </span>
                        </td>
                        <td className="p-2.5 border-b border-slate-100 font-mono text-slate-700 font-medium">
                          {item.travel_date}
                        </td>
                        <td className="p-2.5 border-b border-slate-100 text-slate-700 max-w-[160px] truncate" title={item.from_location || item.from_to}>
                          {item.from_location || item.from_to || '-'}
                        </td>
                        <td className="p-2.5 border-b border-slate-100 text-slate-700 max-w-[160px] truncate" title={item.to_location}>
                          {item.to_location || '-'}
                        </td>
                        <td className="p-2.5 border-b border-slate-100 text-slate-800 max-w-[180px] truncate" title={item.purpose}>
                          {item.purpose || 'Official Duty'}
                        </td>
                        <td className="p-2.5 border-b border-slate-100 text-slate-700 font-medium">
                          {item.travel_mode}
                        </td>
                        <td className="p-2.5 border-b border-slate-100 text-center font-mono text-slate-600">
                          {item.total_km ? `${item.total_km} KM` : '-'}
                        </td>
                        <td className="p-2.5 border-b border-slate-100 text-right font-medium text-slate-900">
                          {formatCurrency(item.claimed_amount)}
                        </td>
                        <td className="p-2.5 border-b border-slate-100 text-right font-semibold text-emerald-700">
                          {formatCurrency(item.approved_amount)}
                        </td>
                        <td className="p-2.5 border-b border-slate-100 text-right font-medium text-amber-700">
                          {formatCurrency(item.advance_amount)}
                        </td>
                        <td className="p-2.5 border-b border-slate-100 text-right font-bold text-blue-700">
                          {formatCurrency(item.payable_amount)}
                        </td>
                        <td className="p-2.5 border-b border-slate-100 text-center">
                          {attachCount > 0 ? (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 border border-slate-300">
                              <Paperclip className="w-3 h-3 text-slate-500" />
                              {attachCount}
                            </span>
                          ) : (
                            <span className="text-slate-400 text-[10px]">-</span>
                          )}
                        </td>
                        <td className="p-2.5 border-b border-slate-100 font-mono text-slate-500 text-[11px]">
                          {item.submitted_date || item.travel_date}
                        </td>
                        <td className="p-2.5 border-b border-slate-100 text-center">
                          <span className={`inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full border ${STATUS_BADGES[item.approval_status]?.cls || STATUS_BADGES['Pending Review'].cls}`}>
                            {item.approval_status || 'Pending'}
                          </span>
                        </td>
                        <td className="p-2.5 border-b border-slate-100 text-center">
                          <span className={`inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full border ${stBadge.cls}`}>
                            {stBadge.label}
                          </span>
                        </td>
                        <td className="p-2.5 border-b border-slate-100 font-mono text-slate-600">
                          {item.payment_date || '-'}
                        </td>
                        <td className="p-2.5 border-b border-slate-100 text-slate-500 max-w-[150px] truncate" title={item.remarks}>
                          {item.remarks || '-'}
                        </td>
                        <td className="p-2.5 border-b border-slate-100 text-center">
                          <button
                            onClick={() => setSelectedRecord(item)}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors border border-blue-200"
                            title="View Complete Travel Details"
                          >
                            <Eye className="w-3.5 h-3.5" />
                            <span>Details</span>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Complete View Details Modal */}
      {selectedRecord && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white z-10">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-emerald-100 text-emerald-700 rounded-xl">
                  <BookOpen className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 text-base">Travel & Conveyance History</h3>
                  <p className="text-xs text-slate-500 font-mono">{selectedRecord.transaction_id} &middot; {selectedRecord.claim_type}</p>
                </div>
              </div>
              <button
                onClick={() => setSelectedRecord(null)}
                className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6 text-xs text-slate-700">
              {/* Header Status Bar */}
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <span className="text-[10px] text-slate-400 uppercase font-semibold block">Submitted Date</span>
                  <span className="font-mono font-semibold text-slate-800">{selectedRecord.submitted_date || selectedRecord.travel_date}</span>
                </div>
                <div>
                  <span className="text-[10px] text-slate-400 uppercase font-semibold block">Approval Status</span>
                  <span className={`inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full border ${STATUS_BADGES[selectedRecord.approval_status]?.cls || STATUS_BADGES['Pending Review'].cls}`}>
                    {selectedRecord.approval_status || 'Pending'}
                  </span>
                </div>
                <div>
                  <span className="text-[10px] text-slate-400 uppercase font-semibold block">Payment Status</span>
                  <span className={`inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full border ${STATUS_BADGES[selectedRecord.payment_status]?.cls || STATUS_BADGES['Pending Review'].cls}`}>
                    {selectedRecord.payment_status || 'Pending'}
                  </span>
                </div>
              </div>

              {/* Journey Details */}
              <div className="space-y-2">
                <h4 className="font-bold text-slate-900 text-xs uppercase tracking-wider text-emerald-600">Journey & Purpose</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 bg-white p-4 rounded-xl border border-slate-200">
                  <div>
                    <span className="text-slate-400 text-[10px] block">Travel Date</span>
                    <span className="font-mono font-medium text-slate-800">{selectedRecord.travel_date}</span>
                  </div>
                  {selectedRecord.return_date && selectedRecord.return_date !== 'N/A' && (
                    <div>
                      <span className="text-slate-400 text-[10px] block">Return Date</span>
                      <span className="font-mono font-medium text-slate-800">{selectedRecord.return_date}</span>
                    </div>
                  )}
                  <div>
                    <span className="text-slate-400 text-[10px] block">From Location</span>
                    <span className="font-medium text-slate-800">{selectedRecord.from_location || selectedRecord.from_to}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 text-[10px] block">To Location</span>
                    <span className="font-medium text-slate-800">{selectedRecord.to_location || '-'}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 text-[10px] block">Travel Mode</span>
                    <span className="font-semibold text-slate-800">{selectedRecord.travel_mode}</span>
                  </div>
                  {selectedRecord.total_km > 0 && (
                    <div>
                      <span className="text-slate-400 text-[10px] block">Distance Recorded</span>
                      <span className="font-mono font-semibold text-slate-800">{selectedRecord.total_km} KM</span>
                    </div>
                  )}
                  <div className="col-span-2 sm:col-span-3">
                    <span className="text-slate-400 text-[10px] block">Purpose of Travel</span>
                    <span className="font-medium text-slate-900">{selectedRecord.purpose}</span>
                  </div>
                </div>
              </div>

              {/* Local Conveyance Itemized Stops (if present) */}
              {Array.isArray(selectedRecord.stops) && selectedRecord.stops.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-bold text-slate-900 text-xs uppercase tracking-wider text-teal-600">On-Duty Stops / Entries ({selectedRecord.stops.length})</h4>
                  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                    <table className="w-full text-left text-xs">
                      <thead className="bg-slate-100 text-slate-600 font-semibold text-[10px] uppercase">
                        <tr>
                          <th className="p-2">From → To</th>
                          <th className="p-2">Mode</th>
                          <th className="p-2 text-right">Distance</th>
                          <th className="p-2 text-right">Amount (₹)</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {selectedRecord.stops.map((stp, sIdx) => (
                          <tr key={sIdx}>
                            <td className="p-2 font-medium">{stp.from_location} → {stp.to_location}</td>
                            <td className="p-2">{stp.mode || stp.travel_mode || '-'}</td>
                            <td className="p-2 text-right font-mono">{stp.km || stp.distance || 0} KM</td>
                            <td className="p-2 text-right font-semibold text-slate-900">{formatCurrency(stp.amount || stp.claimed_amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Expense & Payment Breakdown */}
              <div className="space-y-2">
                <h4 className="font-bold text-slate-900 text-xs uppercase tracking-wider text-blue-600">Financial Breakdown</h4>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <div>
                    <span className="text-slate-500 text-[10px] block">Claimed Amount</span>
                    <span className="font-bold text-slate-900 text-sm">{formatCurrency(selectedRecord.claimed_amount)}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 text-[10px] block">Approved Amount</span>
                    <span className="font-bold text-emerald-700 text-sm">{formatCurrency(selectedRecord.approved_amount)}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 text-[10px] block">Advance Received</span>
                    <span className="font-bold text-amber-700 text-sm">{formatCurrency(selectedRecord.advance_amount)}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 text-[10px] block">Final Payable</span>
                    <span className="font-bold text-blue-700 text-sm">{formatCurrency(selectedRecord.payable_amount)}</span>
                  </div>
                </div>
              </div>

              {/* Audit & Disbursal info */}
              <div className="space-y-2">
                <h4 className="font-bold text-slate-900 text-xs uppercase tracking-wider text-purple-600">Approval & Payment Audit</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 bg-white p-4 rounded-xl border border-slate-200">
                  <div>
                    <span className="text-slate-400 text-[10px] block">Approval Date</span>
                    <span className="font-mono text-slate-800">{selectedRecord.approval_date || '-'}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 text-[10px] block">Approved By</span>
                    <span className="font-medium text-slate-800">{selectedRecord.approved_by || 'Approver'}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 text-[10px] block">Payment Date</span>
                    <span className="font-mono text-slate-800">{selectedRecord.payment_date || '-'}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 text-[10px] block">UTR / Ref Number</span>
                    <span className="font-mono font-semibold text-slate-900">{selectedRecord.utr_reference || '-'}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 text-[10px] block">Payment Method</span>
                    <span className="font-medium text-slate-800">{selectedRecord.payment_method || 'Bank Transfer'}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 text-[10px] block">Processed By</span>
                    <span className="font-medium text-slate-800">{selectedRecord.payment_processed_by || 'Finance'}</span>
                  </div>
                </div>
              </div>

              {/* Attachments */}
              {Array.isArray(selectedRecord.attachments) && selectedRecord.attachments.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-bold text-slate-900 text-xs uppercase tracking-wider text-amber-600">Receipt & Ticket Attachments</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedRecord.attachments.map((att, aIdx) => (
                      <a
                        key={aIdx}
                        href={att.url || att.file_url || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg hover:bg-amber-100 transition-colors text-xs font-medium"
                      >
                        <Paperclip className="w-3.5 h-3.5 text-amber-600" />
                        <span>{att.filename || att.name || `Bill Receipt #${aIdx + 1}`}</span>
                        <ExternalLink className="w-3 h-3 text-amber-500" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Remarks */}
              {selectedRecord.remarks && (
                <div>
                  <span className="text-slate-400 text-[10px] block font-semibold uppercase">Remarks / Notes</span>
                  <div className="bg-slate-50 p-3 rounded-lg border border-slate-200 text-slate-700 italic mt-1">
                    {selectedRecord.remarks}
                  </div>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-slate-100 flex justify-end">
              <Button variant="outline" size="sm" onClick={() => setSelectedRecord(null)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default ESSConveyanceLedger;
